import { useEffect, useState } from 'react'
import { IconAlertTriangle, IconGhost } from '@tabler/icons'
import { UtilityModal, ModalBody } from '../components'
import { ToastNotification } from '../components/Notifications/Toast'
import { fetchFromStorage, randomId, removeFromStorage } from '../utils'

export function useHeaderImpersonation() {
  const [supportGrantActive, setSupportGrantActive] = useState(false)
  const railsAuthTokenRaw =
    typeof fetchFromStorage('userToken') === 'string'
      ? fetchFromStorage('userToken')
      : String(fetchFromStorage('userToken') || '')

  useEffect(() => {
    if (!railsAuthTokenRaw || fetchFromStorage('userImpersonated')) {
      setSupportGrantActive(false)
      return undefined
    }
    let cancelled = false
    fetch(`${process.env.REACT_APP_RAILS}/impersonations/active?token=${encodeURIComponent(railsAuthTokenRaw)}`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        if (!cancelled) setSupportGrantActive(!!d.active)
      })
      .catch(() => {
        if (!cancelled) setSupportGrantActive(false)
      })
    return () => {
      cancelled = true
    }
  }, [railsAuthTokenRaw])

  const endDirectImpersonation = () => {
    removeFromStorage('userImpersonated')
    removeFromStorage('userToken')
    removeFromStorage('userTokenExpiry')
    removeFromStorage('agentToken')
    window.location.href = `${process.env.REACT_APP_RAILS}/end_impersonate`
  }

  const finalizeRequestImpersonation = async (choice, handleModalClose) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_RAILS}/impersonation_sessions/end`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          utk: fetchFromStorage('userToken'),
          choice,
          atk: fetchFromStorage('agentToken'),
        }),
      })
      const data = await res.json().catch(() => ({}))
      removeFromStorage('userImpersonated')
      removeFromStorage('userToken')
      removeFromStorage('userTokenExpiry')
      removeFromStorage('agentToken')
      if (handleModalClose) handleModalClose()
      window.location.href = data.redirect_url || `${process.env.REACT_APP_RAILS}/sites`
    } catch (e) {
      console.error(e)
      ToastNotification({
        id: randomId(),
        icon: 'alert',
        color: 'danger',
        body: 'Could not end session. Try again.',
      })
    }
  }

  const handleStopImpersonationMenu = async () => {
    if (!fetchFromStorage('userImpersonated') || !railsAuthTokenRaw) {
      endDirectImpersonation()
      return
    }
    try {
      const r = await fetch(
        `${process.env.REACT_APP_RAILS}/impersonation_sessions/context?token=${encodeURIComponent(railsAuthTokenRaw)}`,
        { headers: { Accept: 'application/json' } }
      )
      const d = r.ok ? await r.json().catch(() => ({})) : {}
      const isRequestBased = !!(d.request_based ?? d.approval_based)
      if (!isRequestBased) {
        endDirectImpersonation()
        return
      }
      UtilityModal({
        body: ({ handleClose }) => (
          <ModalBody
            handleClose={handleClose}
            title="End support session"
            action="warning"
            cancelButton={false}
            icon={<IconGhost />}
            buttons={[
              {
                label: 'Revoke access',
                action: 'danger',
                className: 'ml-[8px]',
                onClick: () => finalizeRequestImpersonation('revoke', handleClose),
              },
              {
                label: 'Keep access',
                action: 'primary',
                onClick: () => finalizeRequestImpersonation('keep', handleClose),
              },
            ]}
          >
            You are viewing this account as Mailmunch support. End the session here, or revoke access so support
            cannot open another session until you accept a new impersonation request.
          </ModalBody>
        ),
      })
    } catch {
      endDirectImpersonation()
    }
  }

  const revokeCustomerSupportGrant = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_RAILS}/impersonations/revoke`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: railsAuthTokenRaw }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'revoke_failed')
      setSupportGrantActive(false)
      ToastNotification({
        id: randomId(),
        icon: 'tick',
        color: 'success',
        body: 'Support access has been revoked.',
      })
    } catch (e) {
      ToastNotification({
        id: randomId(),
        icon: 'alert',
        color: 'danger',
        body: 'Could not revoke access.',
      })
    }
  }

  const openRevokeSupportGrantConfirmModal = () => {
    UtilityModal({
      body: ({ handleClose }) => (
        <ModalBody
          handleClose={handleClose}
          title="Revoke support access?"
          action="warning"
          icon={<IconAlertTriangle />}
          buttons={[
            {
              label: 'Revoke',
              action: 'danger',
              onClick: () => {
                handleClose()
                revokeCustomerSupportGrant()
              },
            },
          ]}
        >
          Mailmunch support will not be able to start a new session until you approve again.
        </ModalBody>
      ),
    })
  }

  const showRevokeCustomerSupportGrant =
    supportGrantActive && !fetchFromStorage('userImpersonated')

  return {
    handleStopImpersonationMenu,
    showRevokeCustomerSupportGrant,
    openRevokeSupportGrantConfirmModal,
  }
}
